import { useState, useEffect } from 'react';

/**
 * Normalize text for fuzzy comparison:
 * - Collapse whitespace (multiple spaces/newlines → single space)
 * - Normalize curly quotes to straight quotes (\u2018\u2019 → ', \u201C\u201D → ")
 * - Normalize dashes (em-dash \u2014, en-dash \u2013 → hyphen -)
 * - Strip leading/trailing whitespace
 */
export function normalizeText(text) {
  if (!text) return '';
  return text
    .replace(/[\u200B\uFEFF]/g, '')                          // strip zero-width chars
    .replace(/[\u00A0\u2009\u200A\u202F\u205F\u3000]/g, ' ') // Unicode spaces → ASCII space
    .replace(/[\u2018\u2019]/g, "'")                          // curly single quotes
    .replace(/[\u201C\u201D]/g, '"')                          // curly double quotes
    .replace(/[\u2013\u2014]/g, '-')                          // en/em dashes
    .replace(/\s+/g, ' ')                                     // collapse whitespace
    .trim();
}

/**
 * Compute character-level similarity between two strings.
 * Counts matching characters at the same positions divided by the max length.
 */
function similarity(a, b) {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  let matches = 0;
  const minLen = Math.min(a.length, b.length);
  for (let i = 0; i < minLen; i++) {
    if (a[i] === b[i]) matches++;
  }
  return matches / maxLen;
}

/**
 * Expand a match region by walking backward and forward from the initial hit,
 * comparing characters against the full search text (case-insensitive).
 *
 * @param {string} lowerContent - lowercased normalized page content
 * @param {string} lowerSearch - lowercased normalized search text
 * @param {number} initialStart - start index of the initial fragment match in content
 * @param {number} initialEnd - end index of the initial fragment match in content
 * @returns {{ start: number, end: number }}
 */
function expandMatchRegion(lowerContent, lowerSearch, initialStart, initialEnd) {
  // Find where the initial match fragment appears in the search text
  const fragment = lowerContent.substring(initialStart, initialEnd);
  const fragPosInSearch = lowerSearch.indexOf(fragment);
  if (fragPosInSearch === -1) return { start: initialStart, end: initialEnd };

  let newStart = initialStart;
  let newEnd = initialEnd;

  // Walk backward: compare content chars before initialStart with search chars before fragPosInSearch
  let si = fragPosInSearch - 1;
  let ci = initialStart - 1;
  while (si >= 0 && ci >= 0 && lowerContent[ci] === lowerSearch[si]) {
    newStart = ci;
    si--;
    ci--;
  }

  // Walk forward: compare content chars after initialEnd with search chars after fragPosInSearch + fragmentLen
  si = fragPosInSearch + (initialEnd - initialStart);
  ci = initialEnd;
  while (si < lowerSearch.length && ci < lowerContent.length && lowerContent[ci] === lowerSearch[si]) {
    newEnd = ci + 1;
    si++;
    ci++;
  }

  return { start: newStart, end: newEnd };
}

/**
 * Pure fuzzy matching function — separately testable.
 *
 * Slides a window of length equal to the normalized searchText over the
 * normalized textLayerContent, scoring each window by character-level similarity.
 * Returns the best match if its score meets the 80% threshold.
 *
 * @param {string} textLayerContent - concatenated text from the PDF text layer
 * @param {string} searchText - the citation quote to find
 * @returns {{ matchFound: boolean, matchStart: number, matchEnd: number }}
 */
export function fuzzyMatch(textLayerContent, searchText) {
  const noMatch = { matchFound: false, matchStart: -1, matchEnd: -1 };

  const normContent = normalizeText(textLayerContent);
  const normSearch = normalizeText(searchText);

  if (!normSearch || !normContent) return noMatch;

  // Case-insensitive versions for matching
  const lowerContent = normContent.toLowerCase();
  const lowerSearch = normSearch.toLowerCase();

  // ── Fast path: direct substring match (case-insensitive) ──
  const directIdx = lowerContent.indexOf(lowerSearch);
  if (directIdx !== -1) {
    return { matchFound: true, matchStart: directIdx, matchEnd: directIdx + normSearch.length };
  }

  // Try progressively shorter prefixes from the start
  for (const len of [200, 100, 80, 50]) {
    if (lowerSearch.length > len) {
      const shortSearch = lowerSearch.substring(0, len);
      const shortIdx = lowerContent.indexOf(shortSearch);
      if (shortIdx !== -1) {
        const expanded = expandMatchRegion(lowerContent, lowerSearch, shortIdx, shortIdx + len);
        return { matchFound: true, matchStart: expanded.start, matchEnd: expanded.end };
      }
    }
  }

  // Try matching chunks from different positions in the search text
  // Only try this on the SAME page (don't jump to other pages for fragments)
  // Use longer fragments (100 chars) to avoid false positives
  if (lowerSearch.length > 100) {
    const step = Math.floor(lowerSearch.length / 3);
    for (let offset = step; offset < lowerSearch.length - 100; offset += step) {
      const fragment = lowerSearch.substring(offset, offset + 100);
      const fragIdx = lowerContent.indexOf(fragment);
      if (fragIdx !== -1) {
        const expanded = expandMatchRegion(lowerContent, lowerSearch, fragIdx, fragIdx + 100);
        return { matchFound: true, matchStart: expanded.start, matchEnd: expanded.end };
      }
    }
  }

  // ── Slow path: sliding window fuzzy match for LLM-paraphrased text ──
  const windowLen = normSearch.length;
  if (windowLen > normContent.length) {
    const score = similarity(normContent, normSearch);
    if (score >= 0.8) {
      return { matchFound: true, matchStart: 0, matchEnd: normContent.length };
    }
    return noMatch;
  }

  let bestScore = 0;
  let bestStart = -1;

  for (let i = 0; i <= normContent.length - windowLen; i++) {
    const window = normContent.substring(i, i + windowLen);
    const score = similarity(window, normSearch);
    if (score > bestScore) {
      bestScore = score;
      bestStart = i;
    }
  }

  if (bestScore >= 0.8) {
    return { matchFound: true, matchStart: bestStart, matchEnd: bestStart + windowLen };
  }

  // Try a shorter window (75% of search length) in case the citation is a partial match
  const shortWindowLen = Math.floor(normSearch.length * 0.75);
  if (shortWindowLen > 10 && shortWindowLen < windowLen) {
    let shortBestScore = 0;
    let shortBestStart = -1;
    const shortSearch = normSearch.substring(0, shortWindowLen);

    for (let i = 0; i <= normContent.length - shortWindowLen; i++) {
      const window = normContent.substring(i, i + shortWindowLen);
      const score = similarity(window, shortSearch);
      if (score > shortBestScore) {
        shortBestScore = score;
        shortBestStart = i;
      }
    }

    if (shortBestScore >= 0.85) {
      return { matchFound: true, matchStart: shortBestStart, matchEnd: shortBestStart + shortWindowLen };
    }
  }

  return noMatch;
}

/**
 * Custom hook that highlights a fuzzy-matched citation on the react-pdf text layer.
 * Applies dual-color highlighting: yellow for the KB chunk (source passage),
 * pink for the specific citation text shown in the Citations panel.
 *
 * @param {React.RefObject} textLayerRef - ref to the text layer container div
 * @param {string} highlightText - the full KB chunk text to search for
 * @param {boolean} isReady - whether the text layer has finished rendering
 * @param {string} [citationText] - the specific citation snippet to highlight in pink
 * @returns {{ matchFound: boolean, highlightedElement: HTMLElement | null }}
 */
export default function useFuzzyHighlight(textLayerRef, highlightText, isReady, citationText, textLayerVersion) {
  const [result, setResult] = useState({ matchFound: false, highlightedElement: null });

  useEffect(() => {
    if (!isReady || !highlightText || !textLayerRef?.current) {
      setResult({ matchFound: false, highlightedElement: null });
      return;
    }

    const container = textLayerRef.current;
    const spans = Array.from(container.querySelectorAll('span'));

    if (spans.length === 0) {
      setResult({ matchFound: false, highlightedElement: null });
      return;
    }

    // Build concatenated text and track span boundaries
    // Add space between spans if they don't already have whitespace at boundaries
    let concatenated = '';
    const spanBoundaries = []; // { start, end, span }
    for (let i = 0; i < spans.length; i++) {
      const text = spans[i].textContent || '';
      // Add space between spans if needed (no trailing/leading whitespace)
      if (concatenated.length > 0 && text.length > 0) {
        const lastChar = concatenated[concatenated.length - 1];
        const firstChar = text[0];
        if (lastChar !== ' ' && firstChar !== ' ' && lastChar !== '\n' && firstChar !== '\n') {
          concatenated += ' ';
        }
      }
      const start = concatenated.length;
      concatenated += text;
      spanBoundaries.push({ start, end: concatenated.length, span: spans[i] });
    }

    const { matchFound, matchStart, matchEnd } = fuzzyMatch(concatenated, highlightText);

    if (!matchFound) {
      // KB chunk not found on this page — try matching just the citation text (pink only)
      if (citationText && citationText !== highlightText) {
        const citResult = fuzzyMatch(concatenated, citationText);
        if (citResult.matchFound) {
          // Build the origToNormMap for citation-only highlighting
          const normContentCit = normalizeText(concatenated);
          const origTextCit = concatenated;
          const origToNormMapCit = new Array(origTextCit.length).fill(-1);
          let tempNormCit = '';
          let lastWasSpaceCit = false;
          const quoteMapCit = { '\u2018': "'", '\u2019': "'", '\u201C': '"', '\u201D': '"' };
          const dashMapCit = { '\u2013': '-', '\u2014': '-' };
          const zeroWidthCharsCit = new Set(['\u200B', '\uFEFF']);
          const unicodeSpacesCit = new Set(['\u00A0', '\u2009', '\u200A', '\u202F', '\u205F', '\u3000']);

          for (let oi = 0; oi < origTextCit.length; oi++) {
            let ch = origTextCit[oi];
            if (zeroWidthCharsCit.has(ch)) continue;
            if (unicodeSpacesCit.has(ch)) ch = ' ';
            if (quoteMapCit[ch]) ch = quoteMapCit[ch];
            else if (dashMapCit[ch]) ch = dashMapCit[ch];
            const isWs = /\s/.test(ch);
            if (isWs) {
              if (!lastWasSpaceCit && tempNormCit.length > 0) {
                origToNormMapCit[oi] = tempNormCit.length;
                tempNormCit += ' ';
                lastWasSpaceCit = true;
              }
            } else {
              origToNormMapCit[oi] = tempNormCit.length;
              tempNormCit += ch;
              lastWasSpaceCit = false;
            }
          }

          let citOS = -1, citOE = -1;
          for (let oi = 0; oi < origTextCit.length; oi++) {
            if (origToNormMapCit[oi] !== -1 && origToNormMapCit[oi] >= citResult.matchStart && citOS === -1) citOS = oi;
            if (origToNormMapCit[oi] !== -1 && origToNormMapCit[oi] < citResult.matchEnd) citOE = oi + 1;
          }

          if (citOS !== -1 && citOE !== -1) {
            let firstPink = null;
            for (const { start, end, span } of spanBoundaries) {
              if (start < citOE && end > citOS) {
                span.style.backgroundColor = 'rgba(233, 30, 99, 0.35)';
                span.style.border = '2px solid rgba(233, 30, 99, 0.6)';
                if (!firstPink) firstPink = span;
              }
            }
            if (firstPink) firstPink.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setResult({ matchFound: true, highlightedElement: firstPink });
            return () => {
              for (const { span } of spanBoundaries) {
                span.style.backgroundColor = '';
                span.style.border = '';
              }
            };
          }
        }
      }
      setResult({ matchFound: false, highlightedElement: null });
      return;
    }

    // Map the matched character range in the *normalized* space back to spans.
    // We need to figure out which characters in the original concatenated text
    // correspond to the normalized match range.
    //
    // Build a mapping: normalizedIndex → originalIndex
    const normContent = normalizeText(concatenated);
    // Build original→normalized index map
    const origToNorm = [];
    let ni = 0;
    const normalizedChars = normContent.split('');
    // Walk through original text and map to normalized positions
    const origText = concatenated;
    let normIdx = 0;
    const origToNormMap = new Array(origText.length).fill(-1);

    // Re-normalize character by character to build the mapping
    let tempNorm = '';
    let lastWasSpace = false;
    const quoteMap = { '\u2018': "'", '\u2019': "'", '\u201C': '"', '\u201D': '"' };
    const dashMap = { '\u2013': '-', '\u2014': '-' };
    const zeroWidthChars = new Set(['\u200B', '\uFEFF']);
    const unicodeSpaces = new Set(['\u00A0', '\u2009', '\u200A', '\u202F', '\u205F', '\u3000']);

    for (let oi = 0; oi < origText.length; oi++) {
      let ch = origText[oi];

      // Strip zero-width characters (they map to nothing in normalized output)
      if (zeroWidthChars.has(ch)) {
        // This char is removed entirely — no mapping
        continue;
      }

      // Replace Unicode spaces with ASCII space
      if (unicodeSpaces.has(ch)) {
        ch = ' ';
      }

      // Normalize character
      if (quoteMap[ch]) ch = quoteMap[ch];
      else if (dashMap[ch]) ch = dashMap[ch];

      const isWhitespace = /\s/.test(ch);

      if (isWhitespace) {
        if (!lastWasSpace && tempNorm.length > 0) {
          // This whitespace collapses to a single space
          origToNormMap[oi] = tempNorm.length;
          tempNorm += ' ';
          lastWasSpace = true;
        }
        // else: skip (leading whitespace or consecutive whitespace)
      } else {
        origToNormMap[oi] = tempNorm.length;
        tempNorm += ch;
        lastWasSpace = false;
      }
    }

    // Now find original character range that maps to [matchStart, matchEnd)
    let origStart = -1;
    let origEnd = -1;

    for (let oi = 0; oi < origText.length; oi++) {
      if (origToNormMap[oi] !== -1 && origToNormMap[oi] >= matchStart && origStart === -1) {
        origStart = oi;
      }
      if (origToNormMap[oi] !== -1 && origToNormMap[oi] < matchEnd) {
        origEnd = oi + 1;
      }
    }

    if (origStart === -1 || origEnd === -1) {
      setResult({ matchFound: true, highlightedElement: null });
      return;
    }

    // Apply highlights to spans that overlap [origStart, origEnd)
    // If citationText is provided, also find its position for pink highlighting
    let citOrigStart = -1;
    let citOrigEnd = -1;

    if (citationText && citationText !== highlightText) {
      // Find the citation snippet within the page content
      const normCitation = normalizeText(citationText);
      if (normCitation && normCitation.length > 10) {
        const lowerNormContent = normContent.toLowerCase();
        const lowerNormCitation = normCitation.toLowerCase();

        // Try direct substring match first
        let citNormStart = -1;
        let citNormEnd = -1;

        const citIdx = lowerNormContent.indexOf(lowerNormCitation);
        if (citIdx !== -1) {
          citNormStart = citIdx;
          citNormEnd = citIdx + normCitation.length;
        } else {
          // Try progressively shorter prefixes of the citation text
          for (const ratio of [0.9, 0.75, 0.6]) {
            const tryLen = Math.floor(lowerNormCitation.length * ratio);
            if (tryLen < 20) break;
            const tryStr = lowerNormCitation.substring(0, tryLen);
            const tryIdx = lowerNormContent.indexOf(tryStr);
            if (tryIdx !== -1) {
              citNormStart = tryIdx;
              citNormEnd = tryIdx + tryLen;
              break;
            }
          }
          // If prefix didn't work, try suffix and middle portions
          if (citNormStart === -1) {
            for (const ratio of [0.75, 0.6]) {
              const tryLen = Math.floor(lowerNormCitation.length * ratio);
              if (tryLen < 20) break;
              // Try from the end
              const suffixStr = lowerNormCitation.substring(lowerNormCitation.length - tryLen);
              const suffixIdx = lowerNormContent.indexOf(suffixStr);
              if (suffixIdx !== -1) {
                citNormStart = suffixIdx;
                citNormEnd = suffixIdx + tryLen;
                break;
              }
              // Try from the middle
              const midStart = Math.floor((lowerNormCitation.length - tryLen) / 2);
              const midStr = lowerNormCitation.substring(midStart, midStart + tryLen);
              const midIdx = lowerNormContent.indexOf(midStr);
              if (midIdx !== -1) {
                citNormStart = midIdx;
                citNormEnd = midIdx + tryLen;
                break;
              }
            }
          }
          // Last resort: try individual long words (8+ chars) from the citation
          if (citNormStart === -1) {
            const words = lowerNormCitation.split(/\s+/).filter(w => w.length >= 8);
            // Try consecutive word sequences
            for (let wLen = Math.min(words.length, 5); wLen >= 3; wLen--) {
              for (let wi = 0; wi <= words.length - wLen; wi++) {
                const phrase = words.slice(wi, wi + wLen).join(' ');
                if (phrase.length >= 20) {
                  const phraseIdx = lowerNormContent.indexOf(phrase);
                  if (phraseIdx !== -1) {
                    citNormStart = phraseIdx;
                    citNormEnd = phraseIdx + phrase.length;
                    break;
                  }
                }
              }
              if (citNormStart !== -1) break;
            }
          }
        }

        if (citNormStart !== -1 && citNormEnd !== -1) {
          // Map citation normalized range back to original positions
          for (let oi = 0; oi < origText.length; oi++) {
            if (origToNormMap[oi] !== -1 && origToNormMap[oi] >= citNormStart && citOrigStart === -1) {
              citOrigStart = oi;
            }
            if (origToNormMap[oi] !== -1 && origToNormMap[oi] < citNormEnd) {
              citOrigEnd = oi + 1;
            }
          }
        }
      }
    }

    let firstHighlighted = null;
    // First pass: apply yellow to KB chunk region
    for (const { start, end, span } of spanBoundaries) {
      if (start < origEnd && end > origStart) {
        span.style.backgroundColor = 'rgba(255, 235, 59, 0.3)';
        span.style.border = '1px solid rgba(255, 193, 7, 0.6)';
        if (!firstHighlighted) firstHighlighted = span;
      }
    }
    // Second pass: apply pink to citation text region (overrides yellow where they overlap)
    let firstPinkSpan = null;
    if (citOrigStart !== -1 && citOrigEnd !== -1) {
      for (const { start, end, span } of spanBoundaries) {
        if (start < citOrigEnd && end > citOrigStart) {
          span.style.backgroundColor = 'rgba(233, 30, 99, 0.35)';
          span.style.border = '2px solid rgba(233, 30, 99, 0.6)';
          if (!firstPinkSpan) firstPinkSpan = span;
          if (!firstHighlighted) firstHighlighted = span;
        }
      }
    }

    // Scroll target: prefer pink citation, then first highlighted
    let scrollTarget = firstPinkSpan || firstHighlighted;
    if (citOrigStart !== -1 && !firstPinkSpan) {
      // Pink region exists but no spans matched — try finding closest span
      for (const { start, end, span } of spanBoundaries) {
        if (start < citOrigEnd && end > citOrigStart) {
          scrollTarget = span;
          break;
        }
      }
    }

    setResult({ matchFound: true, highlightedElement: scrollTarget || firstHighlighted });

    // Scroll after a brief delay to ensure the page is fully rendered
    const scrollEl = scrollTarget || firstHighlighted;
    if (scrollEl) {
      setTimeout(() => {
        scrollEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }

    // Cleanup: remove highlights when effect re-runs
    return () => {
      for (const { span } of spanBoundaries) {
        span.style.backgroundColor = '';
        span.style.border = '';
      }
    };
  }, [textLayerRef, highlightText, isReady, citationText, textLayerVersion]);

  return result;
}
